import React from 'react';
import { motion } from 'framer-motion';
import { TruckIcon, HeartHandshakeIcon, LeafIcon, ThumbsUpIcon } from 'lucide-react';
import { PageHero } from '../components/PageHero';
import { SectionHeading } from '../components/SectionHeading';
import { CTABand } from '../components/CTABand';
import { SITE } from '../lib/site';
const VALUES = [{
  icon: ThumbsUpIcon,
  title: 'Show Up On Time',
  text: 'When we give you a window, we keep it. Your time matters.'
}, {
  icon: HeartHandshakeIcon,
  title: 'Treat You Right',
  text: 'Friendly, respectful, and careful with your property — every visit.'
}, {
  icon: LeafIcon,
  title: 'Haul Responsibly',
  text: 'We donate and recycle whenever we can so less ends up in the landfill.'
}, {
  icon: TruckIcon,
  title: 'Work Hard',
  text: 'A young crew that isn\'t afraid of the heavy stuff or the dirty jobs.'
}];
const STATS = [{
  value: '2K+',
  label: 'Loads Hauled'
}, {
  value: '5★',
  label: 'Local Rating'
}, {
  value: '100%',
  label: 'Insured'
}, {
  value: 'Same Day',
  label: 'Service'
}];
export function About() {
  return <>
      <PageHero eyebrow="About Us" title={<>Local guys, <span className="text-jk-red" data-id="element-133" data-mp="1pkjnl4vnatbo">big red truck</span></>} subtitle={`Junk Kings started with a red Chevy Silverado, a trailer, and a simple promise: you call, we haul. We're a young, hard-working crew proud to serve ${SITE.area}.`} data-id="element-132" data-mp="1piyor32aso80" />

      <section className="bg-jk-ink py-20" data-id="element-134" data-mp="2dfuoj2iqzy2a">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2" data-id="element-135" data-mp="2det8skqsene8">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} className="overflow-hidden rounded-2xl border border-white/10" data-id="element-136" data-mp="28hb27108sk9e">
            <img src="https://cdn.magicpatterns.com/patterns/generated-images/6847e4f5-0981-48e1-ab30-0820aa7e8e58.jpg" alt="The Junk Kings crew at work" className="h-64 w-full object-cover sm:h-80" data-id="element-137" data-mp="2w6vtq8agopua" />
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} data-id="element-138" data-mp="1ostjggjs14en">
            <h2 className="font-display text-4xl font-extrabold uppercase leading-none text-white" data-id="element-139" data-mp="1xtrpf4drzlzv">
              Built on <span className="text-jk-red" data-id="element-140" data-mp="22ncda3lnmzlc">hustle and honesty</span>
            </h2>
            <p className="mt-4 leading-relaxed text-white/70" data-id="element-141" data-mp="27kcnmqpjeoi9">
              We got tired of seeing folks overcharged and let down by no-shows. So we started Junk Kings to do it
              differently — fair prices, real communication, and a crew that actually shows up ready to work.
            </p>
            <p className="mt-4 leading-relaxed text-white/70" data-id="element-142" data-mp="2lkdldxai9fti">
              Whether it's a single couch or a full estate cleanout, we treat every job like it's for family. That's
              why our neighbors keep calling us back — and telling their friends.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4" data-id="element-143" data-mp="joesmg99bqbb">
              {STATS.map(s => <div key={s.label} className="rounded-xl border border-white/10 bg-jk-panel p-4 text-center" data-id="element-144" data-mp="evv44o917arq">
                  <div className="font-display text-3xl font-extrabold text-jk-red" data-id="element-145" data-mp="9y3b1qwnhily">{s.value}</div>
                  <div className="mt-1 text-xs uppercase tracking-wide text-white/50" data-id="element-146" data-mp="501wnja667gt">{s.label}</div>
                </div>)}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="bg-white py-20" data-id="element-147" data-mp="xnxnw8gyz767">
        <div className="mx-auto max-w-7xl px-4 sm:px-6" data-id="element-148" data-mp="snrtikjbf6kf">
          <SectionHeading light eyebrow="What We Stand For" title={<>The <span className="text-jk-red" data-id="element-150" data-mp="on7ypnm7sf1g">King's code</span></>} data-id="element-149" data-mp="sm8dmv1kmxj6" />
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-151" data-mp="3deahu6ad3vyy">
            {VALUES.map((v, i) => <motion.div key={v.title} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.08
          }} className="rounded-2xl border border-black/10 bg-neutral-50 p-6" data-id="element-152" data-mp="39c5pdavnh5dw" data-mpi={i}>
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-jk-red text-white" data-id="element-153" data-mp="r7e3wqpuovd9">
                  <v.icon className="h-6 w-6" data-id="element-154" data-mp="w2tdv7kqm74e" />
                </div>
                <h3 className="mt-4 font-display text-xl font-bold uppercase text-jk-ink" data-id="element-155" data-mp="1jvs1j5d11poa">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-black/60" data-id="element-156" data-mp="1os8ldnd71hvk">{v.text}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      <CTABand data-id="element-157" data-mp="1euuh0m4jojjm" />
    </>;
}